import FileSaver from 'file-saver'
import React, {useState} from 'react'
import {Modal} from 'react-bootstrap'
import Container from 'react-bootstrap/Container'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {A1_CONSTANTS} from '../..'
import {KTSVG} from '../../../../../gori/components'
import {DEFAULT_PAGE} from '../../../../../gori/constants'
import {fileHelper, toAbsoluteUrl} from '../../../../../gori/helpers'
import {useAuth} from '../../../../../gori/providers'
import {Button} from '../../../../../gori/widgets'
import {buildStandardA1Csv} from '../../utils'
import {A1BatchUploadMappingModal} from './A1BatchUploadMappingModal'
import {AddToA1BatchModal} from './AddToA1BatchModal.tsx'

type Props = {
  show: boolean
  handleClose: () => void
  handleSuccess: () => void
}

const UploadA1Batch: React.FC<Props> = ({show, handleClose, handleSuccess}) => {
  const FILE_EXTENSIONS = [A1_CONSTANTS.UPLOAD_CONFIG.SUPPORTED_FORMATS.CSV.value]

  const intl = useIntl()
  const {currentUser} = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [showModal, setShowModal] = useState<{mapping: boolean; add_to_A1_batch: boolean}>({
    mapping: false,
    add_to_A1_batch: false,
  })
  const [fileInput, setFileInput] = useState<any>(null)
  const [columnsCSV, setColumnsCSV] = useState<any>(null)
  const [mappings, setMappings] = useState<string[] | null>(null)
  const [mappedFile, setMappedFile] = useState<File | null>(null)
  const [buildingFile, setBuildingFile] = useState<boolean>(false)

  // The mapping endpoints are origin-partner only (a destination partner gets a 404), so only
  // origin partners go through the mapping step; everyone else keeps the fixed-template upload.
  const canMapColumns = currentUser?.role === 'origin'

  const handleCloseModal = () => {
    setFileInput(null)
    setColumnsCSV(null)
    setMappings(null)
    setMappedFile(null)
    setShowModal({mapping: false, add_to_A1_batch: false})
    handleClose()
  }

  const setFile = async (e: any) => {
    const file = e.target.files[0]
    if (!file) return

    if (!FILE_EXTENSIONS.includes(file.type)) {
      e.target.value = null
      return toast.error(intl.formatMessage({id: 'INVALID_FILE_TYPE_CSV_ONLY'}))
    }

    const {row, header} = await fileHelper.checkRowHaveData(file)
    if (row > A1_CONSTANTS.UPLOAD_CONFIG.MAX_UPLOAD) {
      e.target.value = null
      return toast.error(
        intl.formatMessage(
          {id: 'CANNOT_UPLOAD_MORE_THAN_INPUT_ITEMS'},
          {input: A1_CONSTANTS.UPLOAD_CONFIG.MAX_UPLOAD}
        )
      )
    }

    if (file.size > A1_CONSTANTS.UPLOAD_CONFIG.SIZE) {
      e.target.value = null
      return toast.warning(
        intl.formatMessage(
          {id: 'FILE_INPUT_ERROR_UPLOADING_PLEASE_UPLOAD_FILES_SMALLER_THAN_8MB'},
          {input: file.name}
        )
      )
    }

    setFileInput(file)
    setColumnsCSV(fileHelper.convertHeaderFile(header))
    e.target.value = null
  }

  const handleSubmit = () => {
    if (!fileInput) return
    setShowModal((prev) =>
      canMapColumns ? {...prev, mapping: true} : {...prev, add_to_A1_batch: true}
    )
  }

  // The backend has no `mappings` parameter, so the mapping is applied here: the picked file is
  // rewritten into the standard 43-column template before it reaches Add to A1 Batch.
  const handleNext = async (nextMappings: string[]) => {
    if (!fileInput) return
    setMappings(nextMappings)
    setBuildingFile(true)
    try {
      const standardFile = await buildStandardA1Csv(fileInput, nextMappings)
      if (!standardFile) {
        toast.error(intl.formatMessage({id: 'FILE_IS_EMPTY'}))
        return
      }
      setMappedFile(standardFile)
      setShowModal((prev) => ({...prev, add_to_A1_batch: true}))
    } finally {
      setBuildingFile(false)
    }
  }

  const handleUploadSuccess = () => {
    if (!(searchParams.get('search_text') === '' && searchParams.get('page') === DEFAULT_PAGE)) {
      searchParams.delete('search_text')
      searchParams.set('page', DEFAULT_PAGE)
      setSearchParams(searchParams)
    }
    handleSuccess()
    handleCloseModal()
  }

  return (
    <>
      {showModal.mapping && (
        <A1BatchUploadMappingModal
          showModal={show && showModal.mapping && !showModal.add_to_A1_batch && !buildingFile}
          // Back returns to the file picker keeping the mapping, Cancel closes the whole flow
          handleBack={(currentMappings) => {
            setMappings(currentMappings)
            setShowModal((prev) => ({...prev, mapping: false}))
          }}
          handleClose={handleCloseModal}
          data={{columnsCSV: columnsCSV}}
          initialMappings={mappings}
          handleNext={handleNext}
        />
      )}
      {showModal.add_to_A1_batch && (
        <AddToA1BatchModal
          show={show && showModal.add_to_A1_batch}
          handleClose={handleCloseModal}
          file={mappedFile ?? fileInput}
          reloadTable={handleUploadSuccess}
        />
      )}
      <Modal
        id='gori_modal_upload_a1_batch'
        tabIndex={-1}
        aria-hidden='true'
        centered
        dialogClassName='mw-650px h-auto'
        show={show && !showModal.mapping && !showModal.add_to_A1_batch}
        backdrop='static'
        onHide={handleCloseModal}
      >
        <div className='modal-content'>
          <Modal.Header closeButton>
            <Modal.Title bsPrefix={'fw-bolder fs-1'}>
              {intl.formatMessage({id: 'UPLOAD_A1_BATCH'})}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className='vh-75 scroll-y'>
            <Container>
              <Button
                event={() =>
                  FileSaver.saveAs(
                    toAbsoluteUrl('/media/gori/a1/A1_Batch_Upload_Template.csv'),
                    'A1 Batch Upload Template.csv'
                  )
                }
                className='btn btn-sm fw-bolder rounded-2 bg-gray-200 bg-hover-primary text-gray-600 text-hover-white'
                label={intl.formatMessage({id: 'A1_BATCH_UPLOAD_TEMPLATE'})}
                children={<KTSVG path='/media/gori/a1/fileDownload.svg' />}
              />
              <div className='dropzone bg-primary-light border-primary border-2 mt-5'>
                <div className='d-flex justify-content-center my-4'>
                  <div>
                    <div className='fs-4 fw-bold text-dark mb-1'>
                      {intl.formatMessage({id: 'DRAG_AND_DROP_HERE_OR'})}
                      <span className='ms-2 text-primary'>
                        {intl.formatMessage({id: 'BROWSE_FILES'})}
                      </span>
                    </div>
                    {!!fileInput ? (
                      <span className='fw-bold text-primary text-decoration fs-5 mt-2'>
                        {fileInput.name}
                      </span>
                    ) : (
                      <div className='d-flex justify-content-center align-items-center'>
                        <Button
                          className='btn btn-secondary mt-4'
                          label={intl.formatMessage({id: 'UPLOAD_FILE'})}
                        />
                      </div>
                    )}
                  </div>
                  <input
                    type='file'
                    id='file-upload'
                    name='file-img'
                    onChange={(e: any) => setFile(e)}
                    className='dropzone__upload mt-n10 cursor-pointer'
                    accept={FILE_EXTENSIONS.join(',')}
                  />
                </div>
              </div>
            </Container>
          </Modal.Body>
          <Modal.Footer>
            <div className='d-flex justify-content-end'>
              <Button
                className='btn btn-secondary me-2'
                label={intl.formatMessage({id: 'CANCEL'})}
                loadingText={intl.formatMessage({id: 'CANCEL'})}
                event={handleCloseModal}
              />
              <Button
                className='btn btn-primary'
                label={intl.formatMessage({id: canMapColumns ? 'NEXT' : 'SUBMIT'})}
                loadingText={intl.formatMessage({id: canMapColumns ? 'NEXT' : 'SUBMIT'})}
                disabled={!fileInput}
                loading={buildingFile}
                event={handleSubmit}
              />
            </div>
          </Modal.Footer>
        </div>
      </Modal>
    </>
  )
}

export {UploadA1Batch}
